import * as PostReducer from "./reducer";
import * as AuthReducer from "../auth/reducer";
import { AppDispatch } from "../store";
import { Collections, CollectionsClass } from "../../utils/firestore/collections";
import { handleErrors } from "../../utils/firestore/handleErrors";
import Clothes, { ClothesTypes } from "../../models/clothes/clothes.model";
import { User } from "../../models/auth/auth.model";
import { collection, getDocs, query, where, orderBy, limit, doc, getDoc, setDoc, updateDoc, deleteDoc, QueryConstraint } from "firebase/firestore";
import type { ClothesFiltersState } from "../../components/ClothesFiltersModal";

class PostAction {

    static getPosts = (
        per_page: number,
        dispatch: AppDispatch
    ) => {
        dispatch(PostReducer.setIsLoading());
        const q = query(
            collection(CollectionsClass.instance, Collections.POSTS),
            orderBy("created_at", "desc"),
            limit(per_page)
        );
        getDocs(q)
            .then(res => dispatch(PostReducer.getPostsSuccess(res.docs.map(d => d.data() as Clothes))))
            .catch((e) => dispatch(PostReducer.setError(handleErrors(e))));
    }

    static getPostsByCategory = (
        per_page: number,
        category: ClothesTypes,
        dispatch: AppDispatch
    ) => {
        dispatch(PostReducer.setIsLoading());
        const q = query(
            collection(CollectionsClass.instance, Collections.POSTS),
            where("type", "==", category),
            limit(per_page)
        );
        getDocs(q)
            .then(res => {
                const items = res.docs.map(d => d.data() as Clothes);
                items.sort((a, b) => (b.created_at ?? 0) - (a.created_at ?? 0));
                dispatch(PostReducer.getPostsByCategorySuccess(items));
            })
            .catch((e) => dispatch(PostReducer.setError(handleErrors(e))));
    }

    static getPostsByName = (
        per_page: number,
        searchName: string,
        dispatch: AppDispatch
    ) => {
        dispatch(PostReducer.setIsLoading());
        const q = query(
            collection(CollectionsClass.instance, Collections.POSTS),
            orderBy("created_at", "desc"),
            limit(per_page)
        );
        getDocs(q)
            .then(res => {
                let data = res.docs.map(d => d.data() as Clothes);
                if (searchName.trim()) {
                    const qLower = searchName.trim().toLowerCase();
                    data = data.filter(rd => rd.name?.toLowerCase().includes(qLower));
                }
                dispatch(PostReducer.getSearchedPostsSuccess(data));
            })
            .catch((e) => dispatch(PostReducer.setError(handleErrors(e))));
    }

    static getPostsByFilter = (
        per_page: number,
        filters: ClothesFiltersState,
        searchName: string,
        dispatch: AppDispatch
    ) => {
        dispatch(PostReducer.setIsLoading());
        const constraints: QueryConstraint[] = [];
        if (filters.category) {
            constraints.push(where("type", "==", filters.category));
        }
        if (filters.gender) {
            constraints.push(where("gender", "in", [filters.gender, "both"]));
        }
        if (filters.size) {
            constraints.push(where("size", "array-contains", filters.size));
        }
        constraints.push(orderBy("created_at", "desc"));
        constraints.push(limit(50));

        const coll = collection(CollectionsClass.instance, Collections.POSTS);
        const q = constraints.length ? query(coll, ...constraints) : query(coll, orderBy("created_at", "desc"), limit(50));
        getDocs(q)
            .then(res => {
                let data = res.docs.map(d => d.data() as Clothes);
                if (filters.colors.length > 0) {
                    data = data.filter(item => item.colors?.some(c => filters.colors.includes(c)));
                }
                const minSell = Number(filters.minSellPrice) || 0;
                const maxSell = Number(filters.maxSellPrice) ?? 99999;
                const minRent = Number(filters.minRentPrice) || 0;
                const maxRent = Number(filters.maxRentPrice) ?? 99999;
                data = data.filter(item => {
                    const sellPrice = item.prices?.isFree ? 0 : (item.prices?.sellPrice ?? 0);
                    const rentPrice = item.prices?.rentPrice ?? 0;
                    return sellPrice >= minSell && sellPrice <= maxSell && rentPrice >= minRent && rentPrice <= maxRent;
                });
                if (searchName.trim()) {
                    const qLower = searchName.trim().toLowerCase();
                    data = data.filter(rd => rd.name?.toLowerCase().includes(qLower));
                }
                data.sort((a, b) => (b.created_at ?? 0) - (a.created_at ?? 0));
                dispatch(PostReducer.getPostsByFilterSuccess(data.slice(0, per_page)));
            })
            .catch((e) => dispatch(PostReducer.setError(handleErrors(e))));
    }

    static getSellerInfo = (
        sellerId: string,
        dispatch: AppDispatch
    ) => {
        dispatch(PostReducer.setIsLoading());
        const docRef = doc(CollectionsClass.instance, Collections.USERS, `user_${sellerId}`);
        getDoc(docRef)
            .then(res => dispatch(PostReducer.getSellerSuccess(res.data() as User)))
            .catch((e) => dispatch(PostReducer.setError(handleErrors(e))));
    }

    static getSellersPosts = (
        seller_id: string,
        dispatch: AppDispatch
    ) => {
        const q = query(
            collection(CollectionsClass.instance, Collections.POSTS),
            where("seller_id", "==", seller_id)
        );
        getDocs(q)
            .then(res => dispatch(PostReducer.getSellersPostsSuccess(res.docs.map(d => d.data() as Clothes))))
            .catch((e) => console.log(handleErrors(e)));
    }

    static getUserPosts = (
        userId: string,
        dispatch: AppDispatch
    ) => {
        dispatch(PostReducer.setIsLoading());
        const q = query(
            collection(CollectionsClass.instance, Collections.POSTS),
            where("seller_id", "==", userId),
        );
        getDocs(q)
            .then(res => dispatch(PostReducer.getUserPostsSuccess(res.docs.map(doc => doc.data() as Clothes))))
            .catch((e) => dispatch(PostReducer.setError(handleErrors(e))));
    }

    static getMyPosts = (
        userId: string,
        dispatch: AppDispatch
    ) => {
        dispatch(PostReducer.setIsLoading());
        const q = query(
            collection(CollectionsClass.instance, Collections.POSTS),
            where("seller_id", "==", userId),
        );
        getDocs(q)
            .then(res => dispatch(PostReducer.getMyPostsSuccess(res.docs.map(doc => doc.data() as Clothes))))
            .catch((e) => dispatch(PostReducer.setError(handleErrors(e))));
    }

    static addPost = (
        user: User,
        post: Clothes,
        dispatch: AppDispatch
    ) => {
        dispatch(PostReducer.setIsLoading());
        const postDocRef = doc(CollectionsClass.instance, Collections.POSTS, `post_${post.seller_id}_${post.id}`);
        const userPosts = user.posts ?? [];
        setDoc(postDocRef, post)
            .then(() => {
                const userDocRef = doc(CollectionsClass.instance, Collections.USERS, `user_${user.id}`);
                return updateDoc(userDocRef, { posts: [...userPosts, post] });
            })
            .then(() => {
                dispatch(PostReducer.addPostSuccess());
                dispatch(AuthReducer.updateUser({ ...user, posts: [...userPosts, post] }));
            })
            .catch(error => dispatch(PostReducer.setError(handleErrors(error))));
    }

    static updatePost = (
        user: User,
        data: Clothes,
        dispatch: AppDispatch
    ) => {
        dispatch(PostReducer.setIsLoading());
        const postDocRef = doc(CollectionsClass.instance, Collections.POSTS, `post_${data.seller_id}_${data.id}`);
        const userPosts = user.posts ?? [];
        const filteredPosts = userPosts.filter((post) => post.id !== data.id);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        updateDoc(postDocRef, data as any)
            .then(() => {
                const userDocRef = doc(CollectionsClass.instance, Collections.USERS, `user_${user.id}`);
                return updateDoc(userDocRef, { posts: [data, ...filteredPosts] });
            })
            .then(() => {
                dispatch(PostReducer.updatePostSuccess());
                dispatch(AuthReducer.updateUser({ ...user, posts: [data, ...filteredPosts] }));
            })
            .catch(error => dispatch(PostReducer.setError(handleErrors(error))));
    }

    static deletePost = (
        user: User,
        data: Clothes,
        dispatch: AppDispatch
    ) => {
        dispatch(PostReducer.setIsLoading());
        const postDocRef = doc(CollectionsClass.instance, Collections.POSTS, `post_${data.seller_id}_${data.id}`);
        const userPosts = user.posts ?? [];
        const filteredPosts = userPosts.filter((post) => post.id !== data.id);
        deleteDoc(postDocRef)
            .then(() => {
                const userDocRef = doc(CollectionsClass.instance, Collections.USERS, `user_${user.id}`);
                return updateDoc(userDocRef, { posts: filteredPosts });
            })
            .then(() => {
                dispatch(PostReducer.deletePostSuccess());
                dispatch(AuthReducer.updateUser({ ...user, posts: filteredPosts }));
            })
            .catch(error => dispatch(PostReducer.setError(handleErrors(error))));
    }

    static clearState = (dispatch: AppDispatch) => {
        dispatch(PostReducer.clearState());
    }

    static clearSeller = (dispatch: AppDispatch) => {
        dispatch(PostReducer.clearSellerData());
    }

    static clearError = (dispatch: AppDispatch) => {
        dispatch(PostReducer.setError(""));
    }
}

export default PostAction;